<template>
    <div class="device-status-component" v-if="!!device">
        <Protector class="protector" v-if="!deviceInfo" />
        <b-container
            :fluid="true"
            class="data-container"
            v-if="!!deviceInfo"
            bg-variant="dark"
            dark
        >
            <b-row class="data">
                <b-col>
                    <b-row>
                        <b-col><span class="label">{{$t('device.status.last_date')}}</span>{{deviceInfo.lastDate}}</b-col>
                    </b-row>
                    <b-row class="bordered">
                        <b-col class="text-center">
                            {{formatString(deviceInfo.coordinates.speed)}} {{$t('device.status.speed.measure')}}
                        </b-col>
                        <b-col class="text-center">
                            {{formatString(deviceInfo.coordinates.height)}} {{$t('device.status.altitude.measure')}}
                        </b-col>
                        <b-col class="text-center">
                            {{formatString(deviceInfo.coordinates.latitude)}} {{formatString(deviceInfo.coordinates.longitude)}}
                        </b-col>
                    </b-row>
                    <b-row class="bordered">
                         <b-col class="text-center">
                             <span class="label">{{$t('device.status.satellites')}}</span>{{calculateSatellites()}}
                         </b-col>
                          <b-col class="text-center">
                             <span class="label">{{$t('device.status.course')}}</span>{{deviceInfo.coordinates.course}}&deg;
                         </b-col>
                    </b-row>
                </b-col>
            </b-row>
            <b-row class="data" v-if="hasAnyVoltage()">
                <b-col>
                    <b-row>
                        <b-col class="group-header text-center" v-bind:class="{collapsed: voltageCollapsed}">
                            {{$t('device.status.voltage')}}
                            <b-icon-caret-down class="collapse-icon" scale="1.5" v-if="voltageCollapsed" @click="toggleVoltage()" />
                            <b-icon-caret-up class="collapse-icon" scale="1.5" v-if="!voltageCollapsed" @click="toggleVoltage()" />
                        </b-col>
                    </b-row>
                    <b-row v-show="!voltageCollapsed">
                        <b-col class="group-header text-center">{{$t('device.status.voltage.internal')}}</b-col>
                        <b-col class="group-header text-center">{{$t('device.status.voltage.external')}}</b-col>
                    </b-row>
                    <b-row class="bordered" v-show="!voltageCollapsed">
                        <b-col class="text-center">{{deviceInfo.voltage.internalVoltage}} V</b-col>
                        <b-col class="text-center">{{deviceInfo.voltage.externalVoltage}} V</b-col>
                    </b-row>
                </b-col>
            </b-row>
            <b-row class="data" v-if="hasAnySimData()">
                <b-col>
                    <b-row>
                        <b-col class="group-header text-center" v-bind:class="{collapsed: simCollapsed}">
                            {{$t('device.status.sim')}}
                            <b-icon-caret-down class="collapse-icon" scale="1.5" v-if="simCollapsed" @click="toggleSim()" />
                            <b-icon-caret-up class="collapse-icon" scale="1.5" v-if="!simCollapsed" @click="toggleSim()" />
                        </b-col>
                    </b-row>
                    <b-row v-show="!simCollapsed">
                        <b-col class="group-header text-center" v-if="!!sim1.length">SIM 1</b-col>
                        <b-col class="group-header text-center" v-if="!!sim2.length">SIM 2</b-col>
                    </b-row>
                    <b-row class="bordered" v-show="!simCollapsed">
                        <b-col v-if="!!sim1.length">
                            <div v-for="status in sim1" :key="status.key">
                                <span class="status-name">{{status.key}}:</span>
                                <span>{{formatString(status.value)}}</span>
                            </div>
                        </b-col>
                        <b-col v-if="!!sim2.length">
                            <div v-for="status in sim2" :key="status.key">
                                <span class="status-name">{{status.key}}:</span>
                                <span>{{formatString(status.value)}}</span>
                            </div>
                        </b-col>
                    </b-row>
                </b-col>
            </b-row>
            <b-row class="data" v-if="hasAnyXnb()">
                <b-col>
                    <b-row>
                        <b-col class="group-header text-center" v-bind:class="{collapsed: xnbCollapsed}">
                            {{$t('device.status.xnb.title')}}
                            <b-icon-caret-down class="collapse-icon" scale="1.5" v-if="xnbCollapsed" @click="toggleXnb()" />
                            <b-icon-caret-up class="collapse-icon" scale="1.5" v-if="!xnbCollapsed" @click="toggleXnb()" />
                        </b-col>
                    </b-row>
                    <b-row class="bordered" v-show="!xnbCollapsed">
                        <b-col class="text-left" v-for="list in xnb" :key="list.key">
                            <span class="status-name">{{list.key}}</span>
                        </b-col>
                    </b-row>
                    <b-row class="bordered" v-show="!xnbCollapsed">
                        <b-col class="text-left" v-for="list in xnb" :key="list.key">
                            <span>{{formatString(list.value)}}</span>
                        </b-col>
                    </b-row>
                </b-col>
            </b-row>
            <b-row class="data" v-if="hasAnyStatus()">
                <b-col>
                    <b-row>
                        <b-col class="group-header text-center" v-bind:class="{collapsed: statusCollapsed}">
                            {{$t('device.status.params.title')}}
                            <b-icon-caret-down class="collapse-icon" scale="1.5" v-if="statusCollapsed" @click="toggleStatus()" />
                            <b-icon-caret-up class="collapse-icon" scale="1.5" v-if="!statusCollapsed" @click="toggleStatus()" />
                        </b-col>
                    </b-row>
                    <b-row class="bordered" v-show="!statusCollapsed">
                        <b-col class="text-left" v-for="(list, index) in statusList" :key="index">
                            <div v-for="status in list" :key="status.key">
                                <span class="status-name">{{status.key}}</span>
                                <span>{{formatString(status.value)}}</span>
                            </div>
                        </b-col>
                    </b-row>
                </b-col>
            </b-row>
            <SensorValues
                v-if="!!sensorConfig"
                :source="deviceInfo"
                :device-config="sensorConfig"
            />
        </b-container>
    </div>
</template>

<script lang="ts">
    import Vue from 'vue';
    import Component from 'vue-class-component';
    import DeviceRepository from "../../Repository/Device/DeviceRepository";
    import {Watch} from "vue-property-decorator";
    import {IDeviceStatusesDto} from "../../Repository/Device/Dto/IDeviceStatusesDto";
    import Protector from "../Common/Protector.vue";
    import SensorValues from "./SensorValues.vue";
    import {SensorInfo, SensorsRepository} from "../../Repository/Sensors/SensorsRepository";
    import {RequestGuard} from '../../utils/RequestGuard';
    import {watchPageVisibility} from '../../utils/PageVisibility';

interface KeyValue {
    key: string,
    value?: string,
}

@Component({
    components: { Protector, SensorValues },
    props: {
        device: { type: Array, required:  false },
    }
})
class DeviceStatus extends Vue {
    deviceInfo: IDeviceStatusesDto | null = null;
    sensorConfig: SensorInfo | null = null;
    statusList: KeyValue[][] = [];
    sim1: KeyValue[] = [];
    sim2: KeyValue[] = [];
    xnb: KeyValue[] = [];
    loaded: boolean = false;
    updateTimer: ReturnType<typeof setInterval> | null = null;
    pollingActive = true;
    requests = new RequestGuard();
    stopWatchingVisibility: (() => void) | null = null;

    created() {
        this.stopWatchingVisibility = watchPageVisibility(this.onPageVisibilityChanged.bind(this));
    }

    onPageVisibilityChanged() {
        if (document.hidden) {
            this.stopUpdate();
        } else if (this.pollingActive) {
            this.onItemChanged();
        }
    }

    voltageCollapsed = false;
    simCollapsed = false;
    statusCollapsed = false;
    xnbCollapsed = false;
    virtualSensorsCollapsed = false;

    deactivated() {
        this.pollingActive = false;
        this.stopUpdate();
    }

    beforeDestroy() {
        this.stopWatchingVisibility?.();
        this.pollingActive = false;
        this.stopUpdate();
    }

    activated() {
        if (!this.pollingActive) {
            this.pollingActive = true;
            this.onItemChanged();
        }
    }

    @Watch('device', {immediate: true})
    onItemChanged() {
        this.deviceInfo = null;
        this.sensorConfig = null;
        this.statusList = [];
        this.sim1 = [];
        this.sim2 = [];
        this.xnb = [];
        this.stopUpdate();
        if (this.$props.device && this.$props.device.length && this.$props.device[0] >= 0) {
            if (!this.pollingActive || document.hidden) {
                return;
            }
            this.loadDeviceInfo();
            this.updateTimer = setInterval(this.loadDeviceInfo.bind(this), 5000);
        } else {
            this.statusList = [];
            this.sim1 = [];
            this.sim2 = [];
            this.xnb = [];
        }
    }

    loadDeviceInfo(): void {
        const deviceId = this.$props.device?.[0];
        if (!this.pollingActive || document.hidden || deviceId === undefined || deviceId < 0) {
            return;
        }
        const request = this.requests.begin();
        if (request === null) {
            return;
        }
        const config = this.requests.config(request);
        const isCurrent = () => this.pollingActive && !document.hidden && this.requests.isCurrent(request) && this.$props.device?.[0] === deviceId;
        const reportError = (error: unknown) => {
            if (isCurrent()) {
                this.$emit('load-error', error);
            }
        };
        const mapObjectToKV = (data: any) => {
            const result = [];
            for (const status in data) {
                if (status === 'date') {
                    continue;
                }
                if (!Object.prototype.hasOwnProperty.call(data, status)) {
                    continue;
                }
                const value = data[status];
                if (value) {
                    result.push({
                        key: status,
                        value: value,
                    });
                }
            }
            return result;
        };

        const sensors = SensorsRepository
            .getOneByDeviceId(this.$store, deviceId, config)
            .then(response => {
                if (isCurrent()) {
                    this.sensorConfig = response.sections.find((q: SensorInfo) => q.type === 'sensors') ?? null;
                }
            })
            .catch(reportError);

        const coordinates = DeviceRepository
            .getFullCoordinatesByDeviceId(deviceId, config)
            .then(response => {
                if (!isCurrent()) {
                    return;
                }
                if (this.deviceInfo && this.deviceInfo.lastDate === response.lastDate) {
                    return;
                }
                let idx = 0;
                let tmp: KeyValue[] = [];
                const statusListTmp: KeyValue[][] = [];

                this.deviceInfo = response;
                for (const status in response.deviceStatus) {
                    if (status === 'date') {
                        continue;
                    }
                    const value = (response.deviceStatus as any)[status];
                    if (value) {
                        if (idx++ === 7) {
                            statusListTmp.push(tmp);
                            tmp = [];
                            idx = 0;
                        }
                        tmp.push({
                            key: status,
                            value: value,
                        });
                    }
                }
                statusListTmp.push(tmp);

                this.statusList = statusListTmp;
                this.sim1 = mapObjectToKV(response.sim1);
                this.sim2 = mapObjectToKV(response.sim2);
                this.xnb = mapObjectToKV(response.xnbRequestData);
            })
            .catch(reportError);

        Promise.all([sensors, coordinates]).then(() => this.requests.finish(request));
    }

    formatString(value?: string): string {
        return value ?? '--';
    }

    calculateSatellites(): number {
        return Number.parseInt(this.deviceInfo?.coordinates.gpsSatellites ?? '0') +
            Number.parseInt(this.deviceInfo?.coordinates.glonassSatellites ?? '0');
    }

    hasAnyStatus() {
        let length = 0;
        this.statusList.forEach(q => length += q.length);
        return length > 0;
    }

    hasAnyVoltage() {
        return !!this.deviceInfo?.voltage?.internalVoltage || !!this.deviceInfo?.voltage?.externalVoltage;
    }

    hasAnySimData() {
        return !!this.sim1.length || !!this.sim2.length;
    }

    hasAnyVirtualSensors(): boolean {
        return !!this.deviceInfo?.virtualSensors;
    }

    hasAnyXnb() {
        return !!this.xnb.length;
    }

    toggleVoltage() {
        this.voltageCollapsed = !this.voltageCollapsed;
    }

    toggleSim() {
        this.simCollapsed = !this.simCollapsed;
    }

    toggleStatus() {
        this.statusCollapsed = !this.statusCollapsed;
    }

    toggleXnb() {
        this.xnbCollapsed = !this.xnbCollapsed;
    }

    toggleVirtualSensors() {
        this.virtualSensorsCollapsed = !this.virtualSensorsCollapsed;
    }

    formatVirtualSensorBool(value?: boolean): string {
        return value ? this.$t('device.status.virtual_sensors.has_value').toString()
            : (value === null || value === undefined)
                ? '--'
                : this.$t('device.status.virtual_sensors.no_value').toString();
    }

    stopUpdate() {
        this.requests.invalidate();
        if (this.updateTimer !== null) {
            clearInterval(this.updateTimer);
            this.updateTimer = null;
        }
    }
}

export default DeviceStatus;
</script>

<style scoped lang="scss">
    .device-status-component {
        position: relative;
        height: calc(100% - 64px);
        max-height: 100%;

        .protector {
            margin-top: 2em;
        }

        .header {
            display: flex;
            justify-content: space-between;
            align-items: center;

            .close-btn {
                cursor: pointer;
            }
        }

        .bordered {
            border: 1px solid white;
        }

        .data-container {
            padding: 0 30px;
        }

        .data {
            padding-top: 1em;

            .label {
                margin-right: 0.5em;
                font-weight: bolder;
            }

            .col {
                .text-center {
                    display: flex;
                    justify-content: center;
                    align-items: center;
                }
            }

            .group-header {
                background-color: gray;
                border: 1px solid white;
                border-bottom: none;
                position: relative;

                &.collapsed {
                    border-bottom: 1px solid white;
                }

                .collapse-icon {
                    position: absolute;
                    right: 15px;
                    cursor: pointer;
                }
            }

            .status-name {
                font-weight: bold;
                margin-right: 0.5em;
            }
        }
    }
</style>
